import React, { useState } from "react"
import { Table, Space, Tooltip, Button, Input, Tag, Typography, Modal, Form } from 'antd'
import { EditOutlined, StarOutlined } from '@ant-design/icons';
import { snakeCase } from 'lodash'

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import { formatDateTime } from "../../helpers/date";

const { Text } = Typography;

const PricingPackageLabelList = props => {
  const { permissions } = usePermissions();
  const [form] = Form.useForm();

  const [editingLabel, setEditingLabel] = useState(null)

  const setDefaultHandler = id => {
    props.onSetDefault(id)
  }

  const openEditModal = (record) => {
    setEditingLabel(record)
    form.setFieldsValue({ name: record.name })
  }

  const closeEditModal = () => {
    setEditingLabel(null)
    form.resetFields()
  }

  const submitEdit = async () => {
    const values = await form.validateFields()

    if (values.name === editingLabel.name) {
      closeEditModal()
      return
    }

    props.onUpdate(editingLabel.objectId, { name: values.name })
    closeEditModal()
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Key",
      dataIndex: "name",
      render: (name) => (
        <Tooltip title="This is the exact value stored on Pricing/Cost rows, and must match the uploaded Excel sheet name.">
          <Text code>{snakeCase(name)}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Default",
      dataIndex: "isDefault",
      render: (isDefault, record) => isDefault ? (
        <Tag color="blue">Default</Tag>
      ) : (
        permissions?.includes(PERMISSIONS.UPDATE_PRICING_PACKAGE_LABEL) && (
          <Tooltip title="Set as default for this category">
            <Button
              size="small"
              icon={<StarOutlined />}
              onClick={() => setDefaultHandler(record.objectId)}
            >
              Set Default
            </Button>
          </Tooltip>
        )
      ),
    },
    {
      title: "Added",
      dataIndex: "createdAt",
      render: (text) => formatDateTime(text),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      render: (text) => formatDateTime(text),
    },
    ...(permissions?.includes(PERMISSIONS.UPDATE_PRICING_PACKAGE_LABEL)
      ? [
          {
            title: "",
            dataIndex: "action",
            fixed: "right",
            render: (_, record) => (
              <Space>
                <Tooltip title="Edit">
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(record)}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="table">
      <Table
        rowKey="objectId"
        className="table-section"
        columns={columns}
        dataSource={props.labels}
        pagination={false}
        scroll={{ x: true }}
        loading={props.loading}
      />

      <Modal
        title="Edit Package Name"
        visible={!!editingLabel}
        onCancel={closeEditModal}
        onOk={submitEdit}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            extra="Must exactly match the Excel sheet name used for this category's pricing upload."
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => (
              <Text type="secondary">
                Key: <Text code>{snakeCase(form.getFieldValue('name') || '')}</Text>
              </Text>
            )}
          </Form.Item>

          <p style={{ marginTop: 12 }}>
            <Text type="warning">
              Renaming changes the key stored on Pricing/Cost rows. Update the Excel sheet name used for this
              category's upload to match, or future uploads won't match this package.
            </Text>
          </p>
        </Form>
      </Modal>
    </div>
  )
}

export default PricingPackageLabelList
